// ContactDetailPage — детальная страница контакта с вкладкой сделок (cross-entity)
// и edit-диалогом (R17a, D12: PUT /api/contacts/{id} с expectedUpdatedAt).
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { CrossEntityTabs, type CrossEntityTab } from './components/CrossEntityTabs';
import { DealStageChip } from './components/DealStageChip';
import { AmountChip } from './components/AmountChip';
import { PaymentBlock } from '@/components/PaymentBlock';
import { contactsApi } from '@/api/contacts';
import { useCompanyMap, resolveCompanyName } from '@/api/companies';
import type { Deal } from '@/api/deals';
import { formatDate } from '@/lib/format';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

const PAGE_SIZE = 50;

/** Детальная страница контакта. */
export function ContactDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  // U-31: компания — часть edit-состояния ('' = «—» = отвязать, канон ContactNewPage).
  const [editValues, setEditValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    notes: '',
    companyId: '',
  });

  const contactQuery = useQuery({
    queryKey: ['contact', id],
    queryFn: () => contactsApi.get(id ?? ''),
    enabled: Boolean(id),
  });

  // R41 / D27 (U-15): резолв названия компании по companyId — не сырой UUID.
  const companyMap = useCompanyMap();

  const editMutation = useMutation({
    mutationFn: () => {
      // B2: optimistic-lock требует фактический updatedAt из данных — без fallback «now».
      if (!contactQuery.data) throw new Error('Данные контакта не загружены');
      return contactsApi.update(id ?? '', {
        firstName: editValues.firstName,
        lastName: editValues.lastName,
        email: editValues.email || null,
        phone: editValues.phone || null,
        position: editValues.position || null,
        notes: editValues.notes || null,
        // U-31: companyId всегда явный ('' → null = отвязать; факт BE ContactService.java:152).
        companyId: editValues.companyId || null,
        expectedUpdatedAt: contactQuery.data.updatedAt,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contact', id] });
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // U-29: селекторы построены на ['contacts-map'] — инвалидация имени после edit.
      void queryClient.invalidateQueries({ queryKey: ['contacts-map'] });
      // U-31: список контактов карточки компании (старой и новой) — до инвалидации
      // response мутации не попадает в кэш, contactQuery.data = значение ДО update.
      const oldCompanyId = contactQuery.data?.companyId ?? null;
      const newCompanyId = editValues.companyId || null;
      if (oldCompanyId !== newCompanyId) {
        if (oldCompanyId) {
          void queryClient.invalidateQueries({ queryKey: ['company-contacts', oldCompanyId] });
        }
        if (newCompanyId) {
          void queryClient.invalidateQueries({ queryKey: ['company-contacts', newCompanyId] });
        }
      }
      setEditOpen(false);
    },
    onError: () => {
      // B2: 409/ошибка → refetch — свежий updatedAt, пользователь не в тупике повторных 409.
      void queryClient.invalidateQueries({ queryKey: ['contact', id] });
    },
  });

  const dealsQuery = useQuery({
    queryKey: ['contact-deals', id],
    queryFn: () => contactsApi.listDealsByContact(id ?? '', 0, PAGE_SIZE),
    enabled: Boolean(id) && activeTab === 'deals',
  });

  if (contactQuery.isLoading) return <LoadingSpinner label="Загрузка контакта…" />;
  if (contactQuery.isError || !contactQuery.data) {
    return (
      <EmptyState
        title="Контакт не найден"
        description={contactQuery.error instanceof Error ? contactQuery.error.message : 'Неизвестная ошибка'}
        actionLabel="К списку контактов"
        onAction={() => navigate('/crm/contacts')}
      />
    );
  }
  const contact = contactQuery.data;

  /** Открывает edit-диалог с текущими значениями контакта. */
  const openEdit = (): void => {
    setEditValues({
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      position: contact.position ?? '',
      notes: contact.notes ?? '',
      companyId: contact.companyId ?? '',
    });
    setEditOpen(true);
  };

  const tabs: CrossEntityTab[] = [
    { id: 'overview', label: 'Обзор' },
    { id: 'deals', label: 'Сделки' },
    // V12 U-35-C: платежи контакта (блок PaymentBlock в отдельной вкладке).
    { id: 'payments', label: t('payments.title') },
  ];

  return (
    <Box>
      <PageHeader
        title={`${contact.firstName} ${contact.lastName}`}
        subtitle={contact.email ?? '—'}
        actions={
          <Button variant="outlined" onClick={openEdit}>
            Редактировать
          </Button>
        }
      />
      {editMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(editMutation.error, 'Не удалось сохранить контакт')}
        </Alert>
      ) : null}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body2">{contact.email ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Телефон
                </Typography>
                <Typography variant="body2">{contact.phone ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Должность
                </Typography>
                <Typography variant="body2">{contact.position ?? '—'}</Typography>
              </Box>
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Заметки
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {contact.notes ?? '—'}
              </Typography>
            </Box>
            {contact.companyId ? (
              <Typography variant="body2" color="text.secondary">
                {/* R41 / D27 (U-15): название компании, не сырой UUID. */}
                Компания: {resolveCompanyName(companyMap, contact.companyId)}
              </Typography>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <CrossEntityTabs tabs={tabs} activeId={activeTab} onChange={setActiveTab} />

      <Box sx={{ pt: 3 }}>
        {activeTab === 'overview' ? (
          <Typography variant="body2" color="text.secondary">
            Создано: {formatDate(contact.createdAt)} · Версия: {contact.version}
          </Typography>
        ) : null}
        {activeTab === 'deals' ? (
          dealsQuery.isLoading ? (
            <LoadingSpinner />
          ) : dealsQuery.isError ? (
            <Alert severity="error">
              {dealsQuery.error instanceof Error ? dealsQuery.error.message : 'Не удалось загрузить сделки'}
            </Alert>
          ) : !dealsQuery.data || dealsQuery.data.content.length === 0 ? (
            <EmptyState title="Нет связанных сделок" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Стадия</TableCell>
                  <TableCell>Сумма</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(dealsQuery.data.content as Deal[]).map((d) => (
                  <TableRow
                    key={d.id}
                    hover
                    onClick={() => navigate(`/crm/deals/${d.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{d.title}</TableCell>
                    <TableCell>
                      <DealStageChip stage={d.stage} />
                    </TableCell>
                    <TableCell>
                      <AmountChip amount={d.amount} currency={d.currency} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : null}

        {activeTab === 'payments' ? <PaymentBlock entityType="CONTACT" entityId={contact.id} /> : null}
      </Box>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Редактировать контакт</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Имя"
              required
              fullWidth
              value={editValues.firstName}
              onChange={(e) => setEditValues((v) => ({ ...v, firstName: e.target.value }))}
              inputProps={{ 'aria-label': 'Имя' }}
            />
            <TextField
              label="Фамилия"
              required
              fullWidth
              value={editValues.lastName}
              onChange={(e) => setEditValues((v) => ({ ...v, lastName: e.target.value }))}
              inputProps={{ 'aria-label': 'Фамилия' }}
            />
            <TextField
              label="Должность"
              fullWidth
              value={editValues.position}
              onChange={(e) => setEditValues((v) => ({ ...v, position: e.target.value }))}
              inputProps={{ 'aria-label': 'Должность' }}
            />
            <TextField
              label="Email"
              fullWidth
              value={editValues.email}
              onChange={(e) => setEditValues((v) => ({ ...v, email: e.target.value }))}
              inputProps={{ 'aria-label': 'Email контакта' }}
            />
            <TextField
              label="Телефон"
              fullWidth
              value={editValues.phone}
              onChange={(e) => setEditValues((v) => ({ ...v, phone: e.target.value }))}
              inputProps={{ 'aria-label': 'Телефон контакта' }}
            />
            <TextField
              label="Заметки"
              fullWidth
              multiline
              minRows={3}
              value={editValues.notes}
              onChange={(e) => setEditValues((v) => ({ ...v, notes: e.target.value }))}
              inputProps={{ 'aria-label': 'Заметки контакта' }}
            />
            {/* U-31: смена/отвязка компании существующего контакта
                (select из useCompanyMap, канон ContactNewPage:179-198; '' = «—» = отвязать). */}
            <TextField
              label="Компания"
              select
              fullWidth
              value={editValues.companyId}
              onChange={(e) => setEditValues((v) => ({ ...v, companyId: e.target.value }))}
              inputProps={{ 'aria-label': 'Компания' }}
            >
              <MenuItem value="">—</MenuItem>
              {[...(companyMap ?? [])].map(([companyId, name]) => (
                <MenuItem key={companyId} value={companyId}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => editMutation.mutate()}
            disabled={editMutation.isPending || !editValues.firstName || !editValues.lastName}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
